import * as d3 from "d3";
import store from "../store/appStore";
import React from "react";
import Draggable from "react-draggable";
import { Paper } from "@material-ui/core";
import { parseJwt } from "./generic";
import moment from "moment";
import { SECTIONS } from "../constants/appConstants";
import _ from "lodash";
import { downloadExcelReportByAPI } from "./downloading";
import { API_URL } from "../constants/api";
import { apiBatchRequest } from "./api_service";
import { getFormattedValues } from "./formatData";

export const controlType = {
  Autofill: "Autofill",
  MUIDatePicker: "MUIDatePicker",
  DateRangePicker: "DateRangePicker",
  DatePicker: "DatePicker",
  DropDown: "Dropdown",
  TextBox: "TextBox",
  MultiSelectDropDown: "MultiSelectDropDown",
  Checkbox: "CheckBox",
  RadioButton: "Radio",
  Switch: "Switch",
  File: "File",
  FileInput: "FileInput",
  PhoneNumber: "PhoneNumber",
  Email: "Email",
  Chip: "Chip",
  JSONTable: "JSON",
  Table: "Table",
  CheckboxList: "CheckboxList",
  ZipCode: "ZipCode",
  Label: "label",
  Button: "Button",
  Year: "Year",
  TextNumber: "TextNumber",
  Badge: "Badge"
};

/**
 * Currency conversion
 * @param {*} number 
 * @param {*} decPlaces 
 * @returns 
 */
function ConvertToCurrencyAbbreviation(number, decPlaces) {
  //2 decimal places => 100, 3 => 1000, etc
  decPlaces = Math.pow(10, decPlaces);

  //Enumerate number abbreviations
  const abbrev = [" K", " M", " B", " T"];

  //Go through the array backWards, so we do the largest first
  for (let i = abbrev.length - 1; i >= 0; i--) {
    //Convert array index to "1000", "1000000", etc
    const size = Math.pow(10, (i + 1) * 3);

    //If the number is bigger or equal do the abbreviation
    if (size <= number) {
      //Here, we multiply by decPlaces, round, and then divide by decPlaces.
      //This gives us nice rounding to a particular decimal place.
      number = Math.round((number * decPlaces) / size) / decPlaces;

      //Handle special case where we round up to the next abbreviation
      if (number == 1000 && i < abbrev.length - 1) {
        number = 1;
        i++;
      }

      //Add the letter for the abbreviation
      number += abbrev[i];

      //We are done... stop
      break;
    }
  }

  return number;
}

/**
 * update configuration depends upon conditions
 * @param {*} columns 
 * @param {*} configuration 
 * @param {*} hiddenConfig 
 * @returns 
 */
function UpdateColumnWithConfiguration(
  columns,
  configuration,
  hiddenConfig = undefined
) {
  const filteredCols = [];
  (columns || []).forEach((c) => {
    const config = (configuration || []).find(
      (x) =>
        _.camelCase(x.dbColumn).toString().toLowerCase().trim() ===
        _.camelCase(c.key).toString().toLowerCase().trim()
    );
    if (config !== undefined) {
      config.disabled = c.disabled !== undefined ? c.disabled : false;
      c = { ...c, ...config };
    }
    if (hiddenConfig !== undefined && c.hideWhen) {
      (c.hideWhen || []).forEach((hideField) => {
        const hiddenConfigItem = hiddenConfig.find(
          (y) => y.key === hideField.key
        );
        if (hiddenConfigItem) {
          if (hiddenConfigItem.value !== hideField.value) {
            filteredCols.push(c);
          }
        } else {
          filteredCols.push(c);
        }
      });
    } else {
      filteredCols.push(c);
    }
  });
  return filteredCols;
}

/**
 * deep clone
 * @param {*} inObject 
 * @returns 
 */
function cloneDeepHelper(inObject) {
  if (inObject === undefined) {
    return undefined;
  }
  let  value, key;
  let outObject;
  if (typeof inObject !== "object" || inObject === null) {
    return inObject; // Return the value if inObject is not an object
  }

  //Create an array or object to hold the values
  // eslint-disable-next-line prefer-const
  outObject = Array.isArray(inObject) ? [] : {};

  for (key in inObject) {
    value = inObject[key];

    //Recursively (deep) copy for nested objects, including arrays
    outObject[key] = cloneDeepHelper(value);
  }

  return outObject;
}

/**
 * get distinct data.
 * @param {*} data 
 * @param {*} key 
 * @returns 
 */
function getDistinctByProperty(data, key) {
  const distinct = [];
  (data || []).forEach((obj) => {

    if (
      distinct.find((x) => x[_.camelCase(key)] === obj[_.camelCase(key)]) ===
      undefined
    ) {
      distinct.push(_.cloneDeep(obj));
    }
  });
  return distinct;
}

export { ConvertToCurrencyAbbreviation, UpdateColumnWithConfiguration, cloneDeepHelper, getDistinctByProperty };

/**
 * Wrap text code
 * @param {*} text 
 * @param {*} width 
 */
function wrapText(text, width) {
  text.each(function () {
    const text = d3.select(this),
      words = text
        .text()
        .split(/\s+/)
        .reverse();
     let word,
      line = [],
      lineNumber = 0;
     const lineHeight = 1.1, // ems
      y = text.attr("y"),
      dy = parseFloat(text.attr("dy"));
     let tspan = text
        .text(null)
        .append("tspan")
        .attr("x", 0)
        .attr("y", y)
        .attr("dy", dy + "em");
    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(" "));
      if (tspan.node().getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(" "));
        line = [word];
        tspan = text
          .append("tspan")
          .attr("x", 0)
          .attr("y", y)
          .attr("dy", ++lineNumber * lineHeight + dy + "em")
          .text(word);
      }
    }
  });
}
export { wrapText };

/**
 * check validation for User Type
 * @returns 
 */
function IsAdmin() {
  const { user } = store.getState();

  if ((user || {}).userData) {
    // const userConfiguration = user.userData || {};
    if (
      (
        (((user.userData || {}).groups || [])[0] || {}).groupName || ""
      ).toLowerCase() == "dc_administrator"
    )
      return true;
  }
  return false;
}
export { IsAdmin };

/**
 * check validation for User Type
 * @returns 
 */
export function IsTenant() {
  const { user } = store.getState();

  if ((user || {}).userData) {
    if (
      (
        (((user.userData || {}).groups || [])[0] || {}).groupName || ""
      ).toLowerCase() == "dc_tenant"
    )
      return true;
  }
  return false;
}

/**
 * check validation for User Type
 * @returns 
 */
export function IsContractor() {
  const { user } = store.getState();

  if ((user || {}).userData) {
    if (
      (
        (((user.userData || {}).groups || [])[0] || {}).groupName || ""
      ).toLowerCase() == "dc_contractor"
    )
      return true;
  }
  return false;
}

/**
 * check validation for User Type
 * @returns 
 */
export function IsTenantAdmin() {
  const { user } = store.getState();

  if ((user || {}).userData) {
    if (user.userData.userInfo && user.userData.userInfo.userType.toLowerCase() == "admin") {
      return true;
    }
  }
  return false;
}

/**
 * sort array
 * @param {*} myArr 
 * @returns 
 */
function sortArray(myArr) {
  const sortedArray = myArr.sort(function (a, b) {
    if (a.toLowerCase() < b.toLowerCase()) return -1;
    if (a.toLowerCase() > b.toLowerCase()) return 1;
    return 0;
  });
  return sortedArray;
}

/**
 * remove duplicates
 * @param {*} myArr 
 * @param {*} prop 
 * @returns 
 */
function removeDuplicates(myArr, prop,) {

  if (prop == undefined) {
    return (myArr || []).filter((obj, pos, arr) => {
      return arr.map((mapObj) => mapObj).indexOf(obj) === pos;
    });
  }
  return (myArr || []).filter((obj, pos, arr) => {
    return arr.map((mapObj) => mapObj[prop]).indexOf(obj[prop]) === pos;
  });
}

/**
 * get user configuration
 * @returns 
 */
function GetUserConfiguration() {
  const { user } = store.getState();
  return user.userData || {};
}

/**
 * fetch User Name
 * @returns 
 */
function GetUserName() {
  let userName = "";
  if (localStorage.getItem("adal.idtoken")) {
    const getData = parseJwt(localStorage.getItem("adal.idtoken"));
    if (getData.given_name !== undefined) {
      userName =
        (getData.given_name || "") + " " + (getData.family_name || "");
    } else if (getData.UserName !== undefined) {
      userName = getData.UserName;
    }
  }
  return userName;
}

/**
 * fetch userEmail
 * @returns 
 */
function GetUserEmail() {
  let userEmail = "";
  if (localStorage.getItem("adal.idtoken")) {
    const getData = parseJwt(localStorage.getItem("adal.idtoken"));
    userEmail = getData.email;
  }
  return userEmail;
}

/**
 * code to check user permission for section
 * @param {*} parentSectionId 
 * @param {*} subSectionId 
 * @returns 
 */
function GetSectionPermission(parentSectionId, subSectionId = 0) {
  const { user } = store.getState();
  const permission = ((user.userData || {}).userPermissions || []).find(
    (x) =>
      x.parentSectionId == parentSectionId &&
      (x.subSectionId == subSectionId ||
        (subSectionId == 0 && x.subSectionId == parentSectionId))
  );

  if (permission != undefined) {
    return permission;
  }
  return {};
}

/**
 * set the visibility of section based on Input parameters.
 * @param {*} parentSectionId 
 * @param {*} subSectionId 
 * @param {*} action 
 * @returns 
 */
function IsSectionVisible(parentSectionId, subSectionId = 0, action = null) {
  const permission = GetSectionPermission(parentSectionId, subSectionId);
  if (permission !== undefined) {
    if (action != null) {
      if (action === SECTIONS.FULL_ACCESS) {
        return permission.accessTypeId === 3;
      }
      else if (action === SECTIONS.ADD_EDIT) {
        return permission.accessTypeId > 1 && permission.accessTypeId <= 3;
      } else if (action === SECTIONS.VIEW) {
        return permission.accessTypeId === 1 || permission.accessTypeId <= 3;
      }
    }
    if (permission.accessTypeId > 0 && permission.accessTypeId < 4) return true;
  }
  return false;
}

/**
 * set the visibility of section based on Input parameters.
 * @param {*} parentSectionId 
 * @param {*} subSectionId 
 * @param {*} action 
 * @returns 
 */
function CheckPermission(parentSectionId, subSectionId = 0, action = null) {
  const permission = GetSectionPermission(parentSectionId, subSectionId);
  if (permission !== undefined) {
    if (action != null) {
      if (action === SECTIONS.FULL_ACCESS) {
        return permission.accessTypeId === 3;
      }
      else if (action === SECTIONS.ADD_EDIT) {
        return permission.accessTypeId > 1 && permission.accessTypeId < 3;
      } else if (action === SECTIONS.VIEW) {
        return permission.accessTypeId === 1;
      }
    }
    if (permission.accessTypeId > 0 && permission.accessTypeId < 4) return true;
  }
  return false;
}
const PaperComponent = (props) => {
  return (
    <Draggable
      handle={"#responsive-dialog-title"}
      cancel={"[class*=\"MuiDialogContent-root\"]"}
    >
      <Paper {...props} />
    </Draggable>
  );
};

/**
 * set number with comma separated values
 * @param {*} num 
 * @returns 
 */
const numberWithCommas = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * set decimal number with comma separated values
 * @param {*} num 
 * @returns 
 */
const decimalNumberWithCommas = (num) => {
  const val = Math.round(Number(num) * 100) / 100;
  const parts = val.toString().split(".");
  return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (parts[1] ? "." + parts[1] : "");
};

/**
 * Required validation check
 * @param {*} value 
 * @param {*} obj 
 * @param {*} formData 
 * @returns 
 */
function isGridFormValid(value, obj, formData) {
  if (obj.isVisible === true) {
    if (value === "Invalid date") {
      obj.isInErrorState = true;
      obj.errorMessage = "Please enter valid date in MM/DD/YYYY format";
      return obj;
    }

    const isRequiredField = checkRequiredValidation(value, obj, true);
    if (isRequiredField !== null) return isRequiredField;
    if (
      obj.dependentValidation !== undefined &&
      obj.dependentValidation.value === formData[obj.dependentValidation.key]
    ) {
      if (value === null || value === undefined || value === "") {
        obj.isInErrorState = true;
        obj.errorMessage = obj.name + " field is required.";
        return obj;
      }
    }
    obj = checkRegValidation(value, obj);
    if (obj.isInErrorState) return obj;
  }
  return CompareValue(value, obj, formData);
}

/**
 * value comparison depends upon conditions
 * @param {*} value 
 * @param {*} obj 
 * @param {*} formData 
 * @returns 
 */
function CompareValue(value, obj, formData) {
  let isValid = true;
  if (value === 0) {
    ((obj.rules || {}).And || []).forEach((rule) => {
      if (rule && isValid) {
        if (rule.key === "") {
          if (formData[obj.key] === 0) {
            obj.isInErrorState = true;
            obj.errorMessage = obj.name + " should be greater than 0";
          }
        }
      }
    });
  }
  if (value != null && value !== "" && value !== undefined) {
    ((obj.rules || {}).And || []).forEach((rule) => {
      if (rule && isValid) {
        if (
          formData[rule.key] === undefined ||
          formData[rule.key] === null ||
          formData[rule.key] === ""
        ) {
          return obj;
        }
        let valueA = null;
        let valueB = null;
        if (
          obj.dataFormat === "Date" ||
          (obj.editConfig || {}).type === controlType.DatePicker
        ) {
          valueA = getFormattedValues(value, "Date", true);
          valueB = getFormattedValues(formData[rule.key], "Date", true);
          valueA = valueA !== "" ? new Date(valueA).getTime() : 0;
          valueB = valueB !== "" ? new Date(valueB).getTime() : 0;
        } else {
          valueA = value;
          valueB = formData[rule.key];
          if (valueA === 0 && valueB === 0) {
            return obj;
          }
        }
        const res = rule.operator(valueA, valueB);
        if (!res.result) {
          obj.isInErrorState = true;
          obj.errorMessage = res.message(obj.name, rule.displayName || "");
          isValid = false;
          return obj;
        } else {
          obj.isInErrorState = false;
          obj.errorMessage = "";
          return obj;
        }
      }
    });
    if (isValid) {
      ((obj.rules || {}).Or || []).forEach((rule) => {
        if (rule) {
          if (!rule.operator(value, formData[rule.key])) {
            obj.isInErrorState = true;
            obj.errorMessage = rule.message(obj.name, rule.displayName || "");
          } else {
            obj.isInErrorState = false;
            obj.errorMessage = "";
          }
        }
      });
    }
  } else {
    obj.isInErrorState = false;
    obj.errorMessage = "";
  }
  return obj;
}

 

/**
 * check required validation depends upon condition.
 * @param {*} value 
 * @param {*} obj 
 * @param {*} isCallFromGrid 
 * @returns 
 */
function checkRequiredValidation(value, obj, isCallFromGrid = false) {
  if (obj.isVisible === true) {
    if (
      obj.isRequired === true &&
      (value === null || value === undefined || value === "")
    ) {
      obj.isInErrorState = true;
      if (obj.dataFormat === "Date") {
        obj.errorMessage =
          (isCallFromGrid ? obj.name : obj.displayName) + " field is required and should be in MM/DD/YYYY format.";
      } else {
        obj.errorMessage =
          (isCallFromGrid ? obj.name : obj.displayName) + " field is required.";
      }
      return obj;
    }
  }
  return null;
}

/**
 * regex validations code
 * @param {*} value 
 * @param {*} obj 
 * @returns 
 */
function checkRegValidation(value, obj) {
  let isValid = true;
  if (obj.isVisible === true) {
    if (obj.dbColumn === "FeePercentage" && obj.dataFormat === "DecimalPercent") {
      const numericInput = new RegExp(
        "^(\\d{1,3}|\\d{1,3}\\.\\d{1,2})$"
      );
      isValid = numericInput.test(value);
    } else if ((obj.dbColumn === "FlatFee" || obj.dbColumn === "MinimumFee") && obj.dataFormat === "MoneyNumber") {
      const numericInput = new RegExp(
        "^(\\d{1,6}|\\d{1,6}\\.\\d{1,2})$"
      );
      isValid = numericInput.test(value);
    } else if (
      obj.dataFormat === "Number" ||
      obj.dataFormat === "MoneyNumber" ||
      obj.dataFormat === "Percent"
    ) {
      const numericInput = new RegExp(
        "^\\s*((\\d+(\\.\\d{0,2})?)|((\\d*(\\.\\d{1,2}))))\\s*$"
      ); // '/^[0-9\b]+$/');
      isValid = numericInput.test(value);
    } else if (obj.dataFormat === "Text" || obj.dataFormat === "MultiText") {
      const textInput = new RegExp("[a-zA-Z_][0-9a-zA-Z_]*");
      isValid = textInput.test(value);
    } else if (obj.dataFormat === "Boolean") {
      if (
        value &&
        (value === "0" || value === "1" || value === true || value === false)
      ) {
        isValid = true;
      } else {
        isValid = false;
      }
    } else if (obj.dataFormat === "Email") {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      isValid = re.test((value || "").toString().toLowerCase());
    } else if (obj.dataFormat === "PhoneNumber") {
      const textInput = /(?=(?:\D*\d){10,15}\D*$)/;
      isValid = textInput.test(value);
    } else if (obj.dataFormat === "ZipCode") {
      if (!isNaN(value)) {
        if (value.length > 4 && value.length < 11) {
          isValid = true;
        } else {
          isValid = false;
        }
      } else {
        isValid = false;
      }
    } else if (obj.dataFormat === "Date") {
      isValid = (obj.minDate ? new Date(value).getTime() >= new Date(obj.minDate).getTime() : true);
      if (isValid === false && obj.isRequired === false && obj.isInErrorState === false) {
        if (value === null) {
          isValid = true;
        }
      }
      obj.isInErrorState = isValid;
      if (isValid === false) {
        obj.isInErrorState = true;
        obj.errorMessage = "Please enter valid date in field.";
        return obj;
      } else {
        obj.isInErrorState = false;
        obj.errorMessage = "";
      }
      isValid = (obj.maxDate ? new Date(value).getTime() <= new Date(obj.maxDate).getTime() : true);
      obj.isInErrorState = isValid;
      if (isValid === false) {
        obj.isInErrorState = true;
        obj.errorMessage = "Please enter valid date in field.";
        return obj;
      } else {
        obj.isInErrorState = false;
        obj.errorMessage = "";
      }
      isValid = (value ? moment(value).format("MM/DD/YYYY") !== "Invalid date" : true);
      obj.isInErrorState = isValid;
      if (isValid === false) {
        obj.isInErrorState = true;
        obj.errorMessage = "Please enter valid date in field.";
      } else {
        obj.isInErrorState = false;
        obj.errorMessage = "";
      }
      isValid = obj?.editConfig?.shouldDisableDate !== undefined ? !(obj?.editConfig?.shouldDisableDate(new Date(value))) : true;
      obj.isInErrorState = isValid;
      if (isValid === false) {
        obj.isInErrorState = true;
        obj.errorMessage = "Please enter valid date in field.";
      } else {
        obj.isInErrorState = false;
        obj.errorMessage = "";
      }
    }

    if (isValid === false) {
      obj.isInErrorState = true;
      obj.errorMessage = "Please enter valid data in field.";
    } else {
      obj.isInErrorState = false;
      obj.errorMessage = "";
    }
  }
  return obj;
}

/**
 * sync status
 * @param {*} status 
 * @returns 
 */
const getSynchStatus = (status) => {
  if (status) {
    const data = status.split("|");
    if (data && data[1]) {
      return (
        <span>{data[0] +
          " " +
          moment(moment.utc(data[1]).toDate()).format("MM/DD/YYYY hh:mm A")}
        </span>);
    }
  }
  return status;
}

/**
 * set order by filter.
 * @param {*} data 
 * @param {*} orderBy 
 * @param {*} order 
 * @returns 
 */
function orderBy(data, orderBy, order) {
  //return _.orderBy(data || [], [orderBy], [order]);
  const notNullValue = (data || []).find(x => x[orderBy] !== undefined && x[orderBy] !== null)?.[orderBy];
  if (notNullValue === undefined || notNullValue === null) {
    //consider it as string
    return _.orderBy(data || [], [orderBy], [order]);
  } else if (!isNaN(notNullValue) && typeof (notNullValue) === "number") {
    //number
    return _.orderBy(data || [], [orderBy], [order]);
  } else if (moment(notNullValue).format("MM/DD/YYYY") !== "Invalid date" && notNullValue?.includes("/")) { // && !isNaN(notNullValue)) {
    //date
    return _.orderBy(data || [], [data => (new Date(data[orderBy])).getTime()], [order]);
  } else {
    //string
    return _.orderBy(data || [], [data => ((data[orderBy]) || "").toString().toLowerCase()], [order]);
  }
}

/**
 * Add current timezone offset
 * @param {*} _date 
 * @returns 
 */
function getDate(_date) {
  const date = new Date(_date);
  return date;
}

/**
 * code to get group parent child list depends upon filter.
 * @param {*} data 
 * @param {*} groupOn 
 * @param {*} orderOn 
 * @param {*} dir 
 * @returns 
 */
function getGroupedParentChildList(data, groupOn,orderOn,dir="desc") {
  if (data === undefined || data.length === 0 || groupOn === undefined || groupOn.length === 0) {
    return data || [];
  }
  const groupData =
    ((_.chain(data) || []
    )
      .groupBy(x => x[groupOn]).map(function (v, i) {
        const datalist = _.orderBy(v || [], [d => d[orderOn]], dir);
        const parentItem = datalist.splice(0, 1)[0];
        return {
          key: i,
          ...parentItem,
          childData: datalist || []
        };
      }).value() || []);
  return groupData;
}

export const standardDateFormat = "MM/DD/YYYY";

/**
 * Check if given value is string or not
 */
function isString(value) {
  return typeof value === "string" || value instanceof String;
}

export {
  isString,
  getGroupedParentChildList,
  getDate,
  sortArray,
  removeDuplicates,
  GetSectionPermission,
  IsSectionVisible,
  CheckPermission,
  GetUserConfiguration,
  GetUserName,
  GetUserEmail,
  PaperComponent,
  numberWithCommas,
  decimalNumberWithCommas,
  isGridFormValid,
  getSynchStatus,
  orderBy
};

/**
 * KPI radio cofig to render the radio
 */
export const kpiRadioSlider = [
  {
    label: "YTD",
    value: "YTD",
    sliderValue: "mtdRadio"
  },
  {
    label: "Yearly",
    value: "YEAR",
    sliderValue: "year"
  },
  {
    label: "Monthly",
    value: "MTD",
    sliderValue: "ytdRadio"
  },
  {
    label: "Monthly",
    value: "MTD",
    showWhen: "staffing",
    sliderValue: "ytdRadio"
  },
  {
    label: "Date Range",
    value: "CDR"
  }
]

/**
 * report radio config to render the radio
 */
export const reportRadioSlider = [
  {
    label: "YTD",
    value: "YTD",
    sliderValue: "mtdRadio"
  },
  {
    label: "Yearly",
    value: "YEAR",
    sliderValue: "year"
  },
  {
    label: "Monthly",
    value: "MTD",
    sliderValue: "ytdRadio"
  },
  {
    label: "Monthly",
    value: "MTD",
    showWhen: "staffing",
    sliderValue: "ytdRadio"
  },
  {
    label: "Date Range",
    value: "CDR"
  },
  {
    label: "None",
    value: "NONE"
  }
]

/**
 * report radio config to render the radio
 */
export const reportRadioRangeSlider = [
  {
    label: "Monthly",
    value: "MTD",
    sliderValue: "ytdRadio"
  },
  {
    label: "YTD",
    value: "YTD",
    sliderValue: "mtdRadio"
  },
  {
    label: "Yearly",
    value: "YEAR",
    sliderValue: "year"
  },
  {
    label: "Monthly",
    value: "MTD",
    showWhen: "staffing",
    sliderValue: "ytdRadio"
  },
  {
    label: "Date Range",
    value: "CDR"
  }
]

export const reportRadioRangeQTRSlider = [
  {
    label: "Monthly",
    value: "MTD",
    sliderValue: "ytdRadio"
  },
  {
    label: "Quarterly",
    value: "QTR",
    sliderValue: "qtdRadio"
  },
  {
    label: "YTD",
    value: "YTD",
    sliderValue: "mtdRadio"
  },
  {
    label: "Yearly",
    value: "YEAR",
    sliderValue: "year"
  },
  {
    label: "Monthly",
    value: "MTD",
    showWhen: "staffing",
    sliderValue: "ytdRadio"
  },
  {
    label: "Date Range",
    value: "CDR"
  }
]


export const reportRadioRangeWeeklySlider = [
  {
    label: "Daily",
    value: "TD",
    sliderValue: "tdRadio"
  },
  {
    label: "Weekly",
    value: "WE",
    sliderValue: "weRadio"
  },
  {
    label: "Monthly",
    value: "MTD",
    sliderValue: "ytdRadio"
  },
  {
    label: "Quarterly",
    value: "QTR",
    sliderValue: "qtdRadio"
  },
  {
    label: "YTD",
    value: "YTD",
    sliderValue: "mtdRadio"
  },
  {
    label: "Yearly",
    value: "YEAR",
    sliderValue: "year"
  },
  {
    label: "Monthly",
    value: "MTD",
    showWhen: "staffing",
    sliderValue: "ytdRadio"
  },
  {
    label: "Date Range",
    value: "CDR"
  }
]

/**
 * Get Quarter Value from date default current
 * @param {*} date 
 * @returns 
 */
export function getQuarter(date = new Date()) {
  return Math.floor(date?.getMonth() / 3 + 1);
}
/**
 * generate the query param according to filter selection
 * @param {*} colFilteredObj -> filter config of report
 * @param {*} filterConfig -> selected config of report
 * @param {*} dateTypeSelected -> type of slider selection
 * @param {*} reportId -> report id
 * @param {*} initialConfigData -> original config data
 * @returns 
 */
export function getQueryParam(colFilteredObj, filterConfig, dateTypeSelected, reportId, initialConfigData) {

  const dateType = dateTypeSelected == "MTD" || dateTypeSelected == "Month"
    ? "Monthly"
    : dateTypeSelected == "YTD"
      ? "YTD"
      : dateTypeSelected == "Year"
        ? "Yearly"
        : dateTypeSelected == "QTR"
          ? "Quarterly"
          : dateTypeSelected == "None"
          ? "None"
          : dateTypeSelected == "CDR"
            ? "Date Range"
            : "Monthly";
  let queryParam = null;
  let firstCall = true;
  const dateOffset = moment().utcOffset();
  colFilteredObj.forEach((n) => {
    filterConfig.forEach((m) => {
      if (m["FilterKeyName"] && m["FilterKeyName"] == n["keyName"]) {
        if (n["value"] == null || n["value"] == undefined || n["value"] == "")
          queryParam = firstCall
            ? "&" + m["ReportParameter"] + "=" + "All"
            : queryParam + "&" + m["ReportParameter"] + "=" + "All";
        else {
          if (m["FilterDataType"] !== "DateRange" &&
            m["FilterDataType"] !== "NullableDateRange" &&
            m["FilterDataType"] !== "DateRangeSlider" &&
            m["FilterDataType"] !== "DateRangeWeeklySlider") {
            const value =
              m["FilterDataType"] == "MultiSelect" ||
                m["FilterDataType"] == "Autocomplete"
                ? getIds(
                  n["value"],
                  n["keyName"],
                  m["optionsList"],
                  "id",
                  initialConfigData
                )
                : n["value"];
            queryParam = firstCall
              ? "&" + m["ReportParameter"] + "=" + value
              : queryParam + "&" + m["ReportParameter"] + "=" + value;
            firstCall = false;
          } else if (m["FilterDataType"] === "DateRange" ||
            m["FilterDataType"] === "NullableDateRange" ||
            m["FilterDataType"] === "DateRangeSlider" ||
            m["FilterDataType"] === "DateRangeWeeklySlider") {
            queryParam = firstCall
              ? getDateRange(m, n, colFilteredObj)
              : queryParam + getDateRange(m, n, colFilteredObj);
            firstCall = false;
          }
        }
      }
    });
  });
  filterConfig.forEach((n) => {
    const exist = colFilteredObj.some(
      (item) => item["keyName"] === n["FilterKeyName"]
    );
    queryParam =
      exist == false && (n["FilterDataType"] != "DateRange" &&
        n["FilterDataType"] != "NullableDateRange" &&
        n["FilterDataType"] != "DateRangeWeeklySlider" &&
        n["FilterDataType"] != "DateRangeSlider")
        ? firstCall
          ? "&" + n["ReportParameter"] + "=" + "All"
          : queryParam + "&" + n["ReportParameter"] + "=" + "All"
        : queryParam;

    queryParam =
      exist == false && (n["FilterDataType"] == "DateRange" ||
        n["FilterDataType"] == "NullableDateRange" ||
        n["FilterDataType"] == "DateRangeWeeklySlider" ||
        n["FilterDataType"] == "DateRangeSlider")
        ? firstCall
          ? getDateRange(
            n,
            colFilteredObj?.find((x) => x.keyName == n["FilterKeyName"])
              ?.value,
            colFilteredObj
          )
          : queryParam +
          getDateRange(
            n,
            colFilteredObj?.find((x) => x.keyName == n["FilterKeyName"])
              ?.value,
            colFilteredObj
          )
        : queryParam;
    firstCall = false;
  });
  queryParam = queryParam + "&DateOffset=" + dateOffset;
  queryParam = reportId == 1 ? queryParam + "&DateRangeType=" + dateType : queryParam;

  return queryParam
}

/**
* get selected date from slider when download the ssrs 
* @param {*} options -> config of selected filter
* @param {*} value -> value
* @param {*} colFilteredObj -> selected config filter data 
* @returns 
*/

function getDateRange(options, value, colFilteredObj) {
  if (
    (colFilteredObj.length == 0 && options?.DatePickerOptional) ||
    colFilteredObj?.find((x) => x.keyName == options.FilterKeyName)?.value ==
    "NONE" ||
    (colFilteredObj.find((x) => x.keyName == options.FilterKeyName) ==
      undefined &&
      options?.DatePickerOptional)
  ) {
    const fromDate = "IsNull=True";
    const toDate = "IsNull=True";
    const reportParams = options.ReportParameter.split(",");
    const queryParam =
      "&" +
      reportParams[0] +
      ":" +
      fromDate +
      "&" +
      reportParams[1] +
      ":" +
      toDate;
    return queryParam;
  } else {
    const dates = value?.value?.split("-");
    const reportParams = options.ReportParameter.split(",");
    const queryParam =
      value != null
        ? "&" +
        reportParams[0] +
        "=" +
        dates[0] +
        "&" +
        reportParams[1] +
        "=" +
        dates[1]
        : "&" +
        reportParams[0] +
        "=" +
        moment()
          .startOf("month")
          .format("MM/DD/YYYY") +
        "&" +
        reportParams[1] +
        "=" +
        moment()
          .endOf("month")
          .format("MM/DD/YYYY");
    return queryParam;
  }
}

/**
 * download ssrs report according to type selection
 * @param {*} report -> report url
 * @param {*} type -> type of report
 * @param {*} queryParam -> ssrs report query param
 * @param {*} fileName -> file name
 * @param {*} reportName -> report file name
 */

export function ssrsReportDownload(report, type = "excel", queryParam, fileName, reportName) {
  const fileNam = fileName + "_" + moment().format("YYYYMMDDHHmmss");

  // const toDate = "IsNull=True";
  switch (type) {
    case "pdf":
      downloadExcelReportByAPI(
        `${report.downloadLink.replace(
          "Replaceable",
          reportName
        )}PDF${queryParam.replace("&AccessGroupId=1", "")}`,
        fileNam,
        "application/pdf"
      );
      break;
    case "excel":
      downloadExcelReportByAPI(
        `${report.downloadLink.replace(
          "Replaceable",
          reportName
        )}EXCELOPENXML${queryParam.replace("&AccessGroupId=1", "")}`,
        fileNam
      );
      break;
    case "mhtml":
      downloadExcelReportByAPI(
        `${report.downloadLink.replace(
          "Replaceable",
          reportName
        )}MHTML${queryParam.replace("&AccessGroupId=1", "")}`,
        fileNam,
        "multipart/related"
      );
      break;
    case "word":
      downloadExcelReportByAPI(
        `${report.downloadLink.replace(
          "Replaceable",
          reportName
        )}WORD${queryParam.replace("&AccessGroupId=1", "")}`,
        fileNam,
        "application/msword"
      );
      break;
  }
}


/**
 * get the selected ids
 * @param {*} data -> options list
 * @param {*} key -> key name
 * @param {*} masterData -> master data list
 * @param {*} id 
 * @param {*} initialConfigData -> original config data
 * @returns 
 */
export function getIds(data, key, masterData, id, initialConfigData, ignoreAll = false) {
  if (masterData && masterData[0]?.filterSourceOn != null) {
    const initialConfig = JSON.parse(
      JSON.stringify([...initialConfigData])
    );
    initialConfig.find((x) => {
      if (x.FilterKeyName == key) {
        masterData = JSON.parse(JSON.stringify([...x.optionsList]));
      }
    });
  }
  let ids = "";
  if (data?.length == masterData?.length + 1 && !ignoreAll) {
    ids = "All";
  } else if (data && data.length > 0) {
    data.map(function (r) {
      if (r != "All") {
        ids +=
          masterData?.filter((f) => f["value"] == r)[0]?.["idValue"] + ",";
      }
    });
  }
  const containsComma = ids.endsWith(",");
  ids = containsComma ? ids.slice(0, -1) : ids;
  return ids;
}



/** 
 * Create report request parameter if filter is applied
 * 
 */
export function getDynamicFilter(selectedList, filterConfig, initialConfigData) {
  const reportParam = [];

  filterConfig?.map(filterData => {
    const filteredKeyData = selectedList.find(x => x.keyName === filterData.FilterKeyName);

    if (filterData.FilterDataType === "Autocomplete") {
      if (reportParam.length === 0) {
        reportParam.push({
          [filterData.ReportParameter]: filteredKeyData !== undefined
            && filteredKeyData?.value?.length > 0
            && filteredKeyData?.value?.find(val => val === "All") === undefined
            ? getIds(filteredKeyData?.value, filterData.ReportParameter, filterData.optionsList, "id", initialConfigData)
            : "All"
        });
      } else {
        reportParam[0][filterData.ReportParameter] = filteredKeyData !== undefined
          && filteredKeyData?.value?.length > 0
          && filteredKeyData?.value?.find(val => val === "All") === undefined
          ? getIds(filteredKeyData?.value, filterData.ReportParameter, filterData.optionsList, "id", initialConfigData)
          : "All";
      }
    } else if (filterData.FilterDataType === "NullableDateRange" ||
      filterData.FilterDataType === "DateRange" ||
      filterData.FilterDataType === "DateRangeWeeklySlider" ||
      filterData.FilterDataType === "DateRangeSlider") {
      const rptParam = filterData.ReportParameter.split(",");
      if (reportParam.length === 0) {
        reportParam.push({
          [rptParam[0]]: filteredKeyData !== undefined && filteredKeyData?.value !== "NONE" && localStorage.getItem("DateRadioSelect") !== "NONE" ? filteredKeyData?.value?.split("-")[0] : "",
          [rptParam[1]]: filteredKeyData !== undefined && filteredKeyData?.value !== "NONE" && localStorage.getItem("DateRadioSelect") !== "NONE" ? filteredKeyData?.value?.split("-")[0] : ""
        });
      } else {
        reportParam[0][rptParam[0]] = filteredKeyData !== undefined && filteredKeyData?.value !== "NONE" && localStorage.getItem("DateRadioSelect") !== "NONE" ? filteredKeyData?.value?.split("-")[0] : "";
        reportParam[0][rptParam[1]] = filteredKeyData !== undefined && filteredKeyData?.value !== "NONE" && localStorage.getItem("DateRadioSelect") !== "NONE" ? filteredKeyData?.value?.split("-")[1] : "";
      }
    } else {
      if (reportParam.length === 0) {
        reportParam.push({
          [filterData.ReportParameter]: filteredKeyData !== undefined ? filteredKeyData?.value : "All"
        });
      } else {
        reportParam[0][filterData.ReportParameter] = filteredKeyData !== undefined ? filteredKeyData?.value : "All";
      }
    }
    return filterData;
  });
  return reportParam;
}

/**
 * get dependent source data depends upon filters
 */
export function getDependentSourceData(optionsList, FilterKeyName, value,state, colFilteredObj){
  const { initialConfigData, filterConfig } = state;
  const configData = filterConfig;
  let isAnyFilterAppliedCount=0;
  
  const initialConfig = JSON.parse(JSON.stringify([...initialConfigData]));
  const initialConfigOriginal = JSON.parse(JSON.stringify([...initialConfigData]));
  if(state.reportSectionId === 7 ){
    colFilteredObj.forEach(x=>{ if(x.keyName !== "LastLoggedOn"){ x.idValue=x.value;
      if (x.value?.length > 0) {
        isAnyFilterAppliedCount++;
      }
     }});
      initialConfig.forEach(f=>{
        const filteredData = [];
        const filteredSelectedData = [];
        if(f.FilterKeyName !== FilterKeyName && value.length>0)
        {
          if((f.DependentFilter?.split("|")||[]).find(k=>k ===FilterKeyName)!=undefined)
          {
            let compareValue =
            colFilteredObj && colFilteredObj.length > 0
              ? colFilteredObj?.find(x => x.keyName == FilterKeyName)?.value
              : value;
          compareValue = compareValue == undefined ? value : compareValue;
          if (compareValue) {
            const filteredIds = getIds(
              compareValue,
              FilterKeyName,
              [...optionsList],
              "id",
              initialConfigData,
              true,
            );
            filteredIds.split(",").forEach(y => {
              const result = f.optionsList.filter(x => {
                const filterIds = JSON.parse(JSON.parse(JSON.stringify(x.filterSourceOn)))[FilterKeyName]?.toString()?.split(",");
                if (filterIds?.length > 1) {
                  for (let i = 0; i < filterIds.length; i++) {
                    if (filterIds[i] == y) return x;
                  }
                } else if (JSON.parse(JSON.parse(JSON.stringify(x.filterSourceOn)))[FilterKeyName]?.toString() == y) return x;
              });
              filteredData.length > 0
                ? filteredData[0]?.forEach(a => {
                  const col = result?.find(
                    mn => mn.idValue === a.idValue && mn.id == a.id
                  );
                  if (col != undefined) {
                    const index = result.indexOf(col);
                    if (index > -1) {
                      result.splice(index);
                    }
                  }
                })
                : null;
              result && filteredData.push(result);
            });
            filteredData?.forEach(m => {
              m?.forEach(n => {
                filteredSelectedData.push(n);
              });
            });
            f.optionsList = filteredSelectedData
              ? filteredSelectedData
              : f.optionsList;

          }
        }
        
      }else{
        if(isAnyFilterAppliedCount > 0){
      f.optionsList=filterConfig.find(x=>x.FilterKeyName===f.FilterKeyName).optionsList;}
       
   } });
    if (isAnyFilterAppliedCount == 1) {
      colFilteredObj.forEach(x=>{ if(x.keyName !== "LastLoggedOn"){
        if (x.value?.length > 0) {
          initialConfig.forEach(ic=> {
            if(ic.FilterKeyName === x.keyName) {
                ic.optionsList = initialConfigOriginal.find(icd => icd.FilterKeyName === x.keyName)?.optionsList || []
            }
          })
        }
       }});
    }
    colFilteredObj.forEach(col => {
      if(col.keyName !== "LastLoggedOn"){
        col.value = col.value.filter(v => {
          const options = initialConfig.find(ic => ic.FilterKeyName === col.keyName)?.optionsList || [];
          if (options.find(op => op.value === v)) {return v}
        })
      }
    })
    
    initialConfigOriginal.forEach(ico => {
      if (ico.DependentFilter?.length > 0) {
        const selectedObj = colFilteredObj.find(x => x.keyName === ico.FilterKeyName);
        if ((selectedObj?.value || []).length > 0) {
          const filters = ico.DependentFilter.split("|");
          initialConfig.forEach(ic => {
            if(filters.find(f => ic.FilterKeyName === f)) {
                ic.optionsList = [... initialConfigOriginal.find(oci=> oci.FilterKeyName === ic.FilterKeyName)?.optionsList || []]
                ic.optionsList = ic.optionsList.filter(v => {
                  const filterIds = JSON.parse(JSON.parse(JSON.stringify(v.filterSourceOn)))[selectedObj.keyName]?.toString();
                  if (selectedObj.value.find(c => c === filterIds)) {return v}                     
                });
            }   
          })
        }
      }
    })
    return {configData:initialConfig,colFilteredObj};
  }else{
  (optionsList[0]?.dependentFilter.split("|") || [])?.map((dependentOn) => {
    configData.forEach(m => {
      const filteredData = [];
      const filteredSelectedData = [];
      if (m.FilterKeyName == dependentOn) {
        initialConfig.find(x => {
          if (x.FilterKeyName == dependentOn) {
            m.optionsList = JSON.parse(JSON.stringify([...x.optionsList]));
          }
        });
        if (
          (colFilteredObj &&
            colFilteredObj.length > 0 &&
            colFilteredObj?.find(x => x.keyName == FilterKeyName)?.value.length > 0) ||
          value.length == 1
        ) {
          let compareValue =
            colFilteredObj && colFilteredObj.length > 0
              ? colFilteredObj?.find(x => x.keyName == FilterKeyName)?.value
              : value;
          compareValue = compareValue == undefined ? value : compareValue;
          if (compareValue) {
            const filteredIds = getIds(
              compareValue,
              FilterKeyName,
              [...optionsList],
              "id",
              initialConfigData,
              true,
            );
            filteredIds.split(",").forEach(y => {
              const result = m.optionsList.filter(x => {
                const filterIds = JSON.parse(JSON.parse(JSON.stringify(x.filterSourceOn)))[FilterKeyName]?.toString()?.split(",");
                if (filterIds?.length > 1) {
                  for (let i = 0; i < filterIds.length; i++) {
                    if (filterIds[i] == y) return x;
                  }
                } else if (JSON.parse(JSON.parse(JSON.stringify(x.filterSourceOn)))[FilterKeyName]?.toString() == y) return x;
              });
              filteredData.length > 0
                ? filteredData[0]?.forEach(a => {
                  const col = result?.find(
                    mn => mn.idValue === a.idValue && mn.id == a.id
                  );
                  if (col != undefined) {
                    const index = result.indexOf(col);
                    if (index > -1) {
                      result.splice(index);
                    }
                  }
                })
                : null;
              result && filteredData.push(result);
            });
            filteredData?.forEach(m => {
              m?.forEach(n => {
                filteredSelectedData.push(n);
              });
            });
            m.optionsList = filteredSelectedData
              ? filteredSelectedData
              : m.optionsList;

            colFilteredObj.forEach(config => {
              if (config.keyName == m.FilterKeyName) {
                try {
                  config.value = m.optionsList.forEach(opt => ((config || {})?.value || [])?.filter(cv => cv === opt.value)) || [];
                  if (config.value.length > 0) {
                    config.idValue = (m.optionsList.forEach(opt => ((config || {})?.idValue?.split(",") || []).filter(cv => cv === opt.idValue)) || []).join(",");
                  }
                  else {
                    config.idValue = undefined;
                  }
                }
                catch (e) {
                  // console.log(e);
                }
              }
            }

            )
          }
        }
      }
    });
  });
  return { configData, colFilteredObj };
}
}

/**
 * notification type function
 */
export const NOTIFICATION_TYPE = {
  Communication: "Communication",
  Request: "Request",
  SecurityIncident:"SecurityIncident"
}

/**
 * code to get communication notification data based on item and type
 * @param {*} item 
 * @param {*} type 
 * @returns 
 */
export const getCommunicationNotificationData = (item,type) => {
  const userDetails = GetUserConfiguration();
  const accessGroupId = userDetails?.groups[0].accessGroupId;
  return new Promise(function (resolve) {
    apiBatchRequest([
      {
        url: API_URL.Communication.GetCommunicationLogByGroup +
          "?accessGroupId=" +
          accessGroupId +
          "&communicationId=" +
          item.recordId,
        key: "content"
      },
      {
        url: API_URL.Communication.GetCommunicationLogRecipient +
          "?communicationId=" +
          item.recordId + "&accessGroupId=" +
          accessGroupId,
        key: "recipient"
      }
    ]).then(({ content, recipient }) => {
      const groupedList = getGroupedParentChildList(content.data.communicationData || [], "parentId", "communicationLogID", "asc");
      if (type === "reply") {
        groupedList.emailRecipients = recipient.data;
        return resolve({ content: groupedList, mainContent: groupedList });
      }
      const selectedContent = content.data.communicationData.filter(x => x.communicationLogID === item.recordId) || [];

      selectedContent[0].emailRecipients = recipient.data;

      return resolve({ content: selectedContent[0], mainContent: groupedList[0] });
    });
  });
}

/**
 * get color code class
 * @param {*} no 
 * @returns 
 */
export function getColorClass(no) {
  if (no <= 30 && no >= 0) return "alertText";
  else if (no <= 90 && no > 30) return "warningText";
  else if (no > 90) return "okText";
  else return "";
}

/**
 * get tooltip text
 */

export function getToolTipText (data, key) {
  return data?.find(
    b => b.UiColumnName == key
  )?.Tooltip || "";
}


export function validateEmail (value) {
  const pattern = new RegExp(/^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i);
  const isValid =  pattern.test(value)
  const obj = {};
  obj.isInErrorState = isValid;
  if (isValid === false) {
    obj.isInErrorState = true;
    obj.errorMessage = "Please enter valid data in field.";
    return obj;
  } else {
    obj.isInErrorState = false;
    obj.errorMessage = "";
  }
  return obj;
}

/**
 * get timezone of current environment
 * @returns 
 */

export function getTimeZone () {
  
 return  Intl.DateTimeFormat().resolvedOptions().timeZone +
  " " +
  new Date()
    .toLocaleTimeString("en-us", { timeZoneName: "short" })
    .split(" ")[2];
}

/**
 * Get Start date and end date
 * @param {*} value 
 */
export function quarterDateData (value, year) {
  let startDate = null;
  let endDate = null;
  // Quarter 1 is selected
  if (value == 1) {
    startDate = moment(year)
      .month(0)
      .startOf("month");
    endDate = moment(year)
      .month(2)
      .endOf("month");
  } else if (value == 2) {
  // Quarter 2 is selected
    startDate = moment(year)
      .month(3)
      .startOf("month");
    endDate = moment(year)
      .month(5)
      .endOf("month");
  } else if (value == 3) {
  // Quarter 3 is selected
    startDate = moment(year)
      .month(6)
      .startOf("month");
    endDate = moment(year)
      .month(8)
      .endOf("month");
  } else if (value == 4) {
  // Quarter 4 is selected
    startDate = moment(year)
      .month(9)
      .startOf("month");
    endDate = moment(year)
      .month(11)
      .endOf("month");
  }

  return {
    startDate,
    endDate
  };
}

export function a11yProps(index) {
  return {
    id: `scrollable-prevent-tab-${index}`,
    "aria-controls": `scrollable-prevent-tabpanel-${index}`,
  };
}

/**
 * get disabled property
 * @param {*} sectionId 
 * @returns 
 */

export function GetPropertyVisibility(sectionId) {
 
  const { user } = store.getState();

  const propertyDisabled = ((user.userData || {}).propertyVisibility || []).filter(
    (x) =>
      x.sectionId == sectionId &&
      x.accessTypeId == 4);

  return (propertyDisabled || []).map(x=> x.propertyId);
}

/**
 * get active property
 * @param {*} dataCenterList 
 * @param {*} sectionId 
 * @returns 
 */

export function GetEnableProperty(dataCenterList, sectionId, key = "propertyID") {
  const disabledProperty = GetPropertyVisibility(sectionId);
  return (dataCenterList || []).filter(x=> disabledProperty.find(p => p == x[key]) == undefined);
}